/**
 * The capture queue. Everything here exists because of where capture happens:
 * standing at a till, one-handed, on a connection that may or may not be there.
 *
 * Two rules shape all of it.
 *
 * **The UI never waits on the network.** `capture_id` is minted first, the
 * photo blob and sidecar are written to IndexedDB, and only then does an upload
 * start. Pressing save is a local write; if the phone dies between the shop and
 * home, the receipt is still there.
 *
 * **A 4xx must not poison the queue.** 5xx, timeouts and offline retry with
 * backoff indefinitely, because those are the server's problem and it will come
 * back. A 4xx is this capture's problem and retrying it forever means the queue
 * never drains and every later receipt is stuck behind it. So 4xx fails
 * immediately, keeps the server's `error.code` for a one-tap repair, and the
 * flush moves on.
 *
 * `rate_limited` is the exception that proves the rule, which is why the server
 * marks it `retryable: true` rather than leaving clients to infer from the
 * status. A reconnect burst is the one case guaranteed to trip a limit, so the
 * flush is serialised with gaps rather than parallel.
 */
const DB_NAME = "grocery-capture";
const DB_VERSION = 1;
const STORE = "queue";

export type QueueState = "queued" | "uploading" | "retrying" | "failed" | "done";

export interface Sidecar {
  capture_id: string;
  captured_at: string;
  store: string;
  card_total: number | null;
  currency: string;
  page_of: string | null;
  note: string | null;
}

export interface QueueItem {
  capture_id: string;
  sidecar: Sidecar;
  /**
   * The photo, as bytes rather than a Blob.
   *
   * IndexedDB is specified to store Blobs, and Safari has historically got that
   * wrong in ways that lose the data silently - which on iOS, a target platform
   * here, would mean a queue full of receipts with no photos in them. An
   * ArrayBuffer round-trips through structured clone everywhere.
   *
   * Found because the test shim also failed to round-trip a Blob. The shim's
   * bug is not Safari's bug, but it pointed at a real one.
   */
  photo: ArrayBuffer;
  photo_type: string;
  state: QueueState;
  attempts: number;
  /** Kept verbatim from the server so the repair UI can branch on it, and so a
   *  human reading the queue sees why rather than "failed". */
  error_code?: string;
  error_message?: string;
  next_attempt_at?: number;
  created_at: number;
}

/** ULID-ish: time-ordered prefix plus randomness. Ordering matters because the
 *  queue is drained oldest-first and a lexically sortable id keeps that true
 *  without a separate index. */
export function mintCaptureId(now = Date.now()): string {
  const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
  let time = "";
  let t = now;
  for (let i = 0; i < 10; i += 1) {
    time = ALPHABET[t % 32]! + time;
    t = Math.floor(t / 32);
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let rand = "";
  for (const b of bytes) rand += ALPHABET[b % 32];
  return `cap_${time}${rand}`;
}

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "capture_id" });
        store.createIndex("state", "state");
        store.createIndex("created_at", "created_at");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export async function enqueue(sidecar: Sidecar, photo: Blob): Promise<QueueItem> {
  const item: QueueItem = {
    capture_id: sidecar.capture_id,
    sidecar,
    photo: await photo.arrayBuffer(),
    photo_type: photo.type || "image/jpeg",
    state: "queued",
    attempts: 0,
    created_at: Date.now(),
  };
  await tx("readwrite", (s) => s.put(item));
  return item;
}

/** Rebuild the Blob for upload. The bytes are what was stored; the type is
 *  carried alongside because an ArrayBuffer does not remember it. */
export function photoBlob(item: QueueItem): Blob {
  return new Blob([item.photo], { type: item.photo_type });
}

export async function all(): Promise<QueueItem[]> {
  const items = await tx<QueueItem[]>("readonly", (s) => s.getAll());
  return items.sort((a, b) => a.created_at - b.created_at);
}

/** What the header counts: anything not yet accepted by the server. `failed`
 *  is included on purpose - it still needs you, and hiding it would make the
 *  count a comfort rather than a fact. */
export async function pending(): Promise<QueueItem[]> {
  return (await all()).filter((i) => i.state !== "done");
}

export async function remove(captureId: string): Promise<void> {
  await tx("readwrite", (s) => s.delete(captureId));
}

async function update(item: QueueItem, patch: Partial<QueueItem>): Promise<QueueItem> {
  const next = { ...item, ...patch };
  await tx("readwrite", (s) => s.put(next));
  return next;
}

export interface UploadResult {
  ok: boolean;
  duplicate?: boolean;
  status: number;
  code?: string;
  message?: string;
  retryable: boolean;
}

export type Uploader = (item: QueueItem) => Promise<UploadResult>;

/** Exponential, capped. Capped because an hour-long backoff on a phone that has
 *  been in a pocket all day means the queue does not drain when you get home. */
export function backoffMs(attempts: number): number {
  return Math.min(2 ** Math.min(attempts, 6) * 1000, 60_000);
}

export interface FlushReport {
  uploaded: number;
  duplicates: number;
  failed: number;
  deferred: number;
}

/**
 * Drain the queue, oldest first, one at a time.
 *
 * Serialised deliberately. A phone coming back online wants to fire everything
 * at once, and a parallel burst is the single most reliable way to trip the
 * rate limit - turning a working reconnect into a queue of 429s.
 */
export async function flush(upload: Uploader, now = Date.now()): Promise<FlushReport> {
  const report: FlushReport = { uploaded: 0, duplicates: 0, failed: 0, deferred: 0 };

  for (const item of await pending()) {
    if (item.state === "failed") continue; // needs a person, not another attempt
    if (item.next_attempt_at && item.next_attempt_at > now) {
      report.deferred += 1;
      continue;
    }

    const attempt = await update(item, { state: "uploading", attempts: item.attempts + 1 });
    let result: UploadResult;
    try {
      result = await upload(attempt);
    } catch (err) {
      // No response at all: offline, DNS, TLS. Indistinguishable from a request
      // that succeeded and lost its reply - which is why capture_id was minted
      // before the first attempt, so a retry is answered rather than duplicated.
      result = {
        ok: false,
        status: 0,
        retryable: true,
        message: err instanceof Error ? err.message : String(err),
      };
    }

    if (result.ok) {
      // Accepted by the server, which now owns it. Dropping the local copy is
      // safe only because that is true - BUILD-SPEC 4.6 step 2.
      await remove(item.capture_id);
      if (result.duplicate) report.duplicates += 1;
      else report.uploaded += 1;
      continue;
    }

    if (result.retryable) {
      await update(attempt, {
        state: "retrying",
        next_attempt_at: now + backoffMs(attempt.attempts),
        error_code: result.code,
        error_message: result.message,
      });
      report.deferred += 1;
      continue;
    }

    // 4xx. This one is broken and no amount of waiting fixes it, so it stops
    // being retried and starts being visible.
    await update(attempt, {
      state: "failed",
      error_code: result.code ?? "invalid_request",
      error_message: result.message,
      next_attempt_at: undefined,
    });
    report.failed += 1;
  }

  return report;
}

/** After a one-tap repair: clear the error and let the flush pick it up again. */
export async function retryFailed(captureId: string): Promise<void> {
  const item = await tx<QueueItem | undefined>("readonly", (s) => s.get(captureId));
  if (!item) return;
  await update(item, {
    state: "queued",
    error_code: undefined,
    error_message: undefined,
    next_attempt_at: undefined,
  });
}

export async function patchSidecar(captureId: string, patch: Partial<Sidecar>): Promise<void> {
  const item = await tx<QueueItem | undefined>("readonly", (s) => s.get(captureId));
  if (!item) return;
  await update(item, {
    sidecar: { ...item.sidecar, ...patch },
    state: "queued",
    error_code: undefined,
    error_message: undefined,
  });
}
